/**
 * Encryption utilities for CRM integration
 * 
 * In a production environment, this would use a proper encryption library
 * and secure key management. For this demo, we're using a simple
 * base64 encoding as a placeholder.
 */

/**
 * Encrypt sensitive data
 * @param data The data to encrypt
 * @returns Encrypted data
 */
export function encryptData(data: string): string {
  // In a real implementation, this would use a proper encryption algorithm
  // For this demo, we'll just use base64 encoding as a placeholder
  try {
    return btoa(`encrypted:${data}`);
  } catch (error) {
    console.error('Error encrypting data:', error);
    return `encrypted:${data}`;
  }
}

/**
 * Decrypt sensitive data
 * @param encryptedData The encrypted data
 * @returns Decrypted data
 */
export function decryptData(encryptedData: string): string {
  // In a real implementation, this would use a proper decryption algorithm
  // For this demo, we'll just use base64 decoding as a placeholder
  try {
    const decoded = atob(encryptedData);
    return decoded.startsWith('encrypted:') ? decoded.substring(10) : decoded;
  } catch (error) {
    console.error('Error decrypting data:', error);
    return encryptedData.startsWith('encrypted:') ? encryptedData.substring(10) : encryptedData;
  }
}

/**
 * Generate a secure random key
 * @returns A secure random key
 */
export function generateSecureKey(): string {
  // In a real implementation, this would generate a cryptographically secure key
  // For this demo, we'll just generate a random string
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Hash a password or sensitive value
 * @param value The value to hash
 * @returns Hashed value
 */
export function hashValue(value: string): string {
  // In a real implementation, this would use a proper hashing algorithm
  // For this demo, we'll just use a simple hash function
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(16);
}